/**
 * Admin user table: analysis caps from API (`/api/auth/role-compute-presets`) match server DB rules.
 */
import type { UserPatchBody } from '../services/adminService';
import type { RoleComputePresetDTO } from '../types/auth';
import { BILLING_TIERS, packMonthlyMs, tierKeyFromRole } from './billingProfile';

export type AdminComputeMode = 'daily' | 'monthly' | 'unlimited';

export function tierKeyFromRoleForPreset(role: string | undefined): string {
  return tierKeyFromRole(role);
}

export function presetForRole(presets: RoleComputePresetDTO[], role: string | undefined): RoleComputePresetDTO {
  const key = tierKeyFromRole(role);
  const p = presets.find((x) => x.role_key === key);
  if (p) return p;
  const tier = BILLING_TIERS[key as keyof typeof BILLING_TIERS] ?? BILLING_TIERS.blo;
  return {
    role_key: key,
    daily_hours: tier.baseHoursPerDay,
    monthly_pack_hours: tier.monthlyPackHours,
    max_monthly_extra_hours: tier.monthlyPackHours,
    price_base_usd: tier.usd.base,
    price_pack_usd: tier.usd.pack,
    price_extra_per_hour_usd: tier.usd.extraPerHour,
    price_unlimited_usd:
      key === 'lgu' || key === 'project_developer' ? 500 : key === 'plantation_owner' ? 260 : 100,
  };
}

/** Build PATCH body for signed-in users from plan controls (not for guest). */
export function buildAdminComputePlanPatch(
  role: string,
  mode: AdminComputeMode,
  extraWholeHours: number,
  presets: RoleComputePresetDTO[]
): UserPatchBody {
  const p = presetForRole(presets, role);
  const packMs = p.monthly_pack_hours * 3_600_000;
  const dayMs = Math.round(p.daily_hours * 3_600_000);

  if (mode === 'daily') {
    return {
      compute_limit_mode: 'daily',
      compute_budget_ms_per_day: dayMs,
      compute_budget_ms_per_month: null,
    };
  }
  if (mode === 'monthly') {
    const eh = Math.max(0, Math.min(Math.floor(extraWholeHours), p.max_monthly_extra_hours));
    return {
      compute_limit_mode: 'monthly',
      compute_budget_ms_per_day: null,
      compute_budget_ms_per_month: packMs + eh * 3_600_000,
    };
  }
  return {
    compute_limit_mode: 'unlimited',
    compute_budget_ms_per_day: null,
    compute_budget_ms_per_month: null,
  };
}

const ROLE_LABEL: Record<string, string> = {
  blo: 'BLO (big land owner)',
  plantation_owner: 'Plantation owner',
  lgu: 'LGU',
  project_developer: 'Carbon Project Developer',
};

export function presetSummaryLabel(
  role: string,
  mode: AdminComputeMode,
  presets: RoleComputePresetDTO[]
): string {
  const p = presetForRole(presets, role);
  const seg = ROLE_LABEL[p.role_key] || p.role_key;
  if (mode === 'daily') {
    return `${p.daily_hours} h/day UTC (${seg} rules)`;
  }
  if (mode === 'monthly') {
    return `${p.monthly_pack_hours} h/mo pack + extra (${seg})`;
  }
  return `Unlimited · no monthly analysis cap (${seg})`;
}

/** Derive whole extra hours from stored monthly budget vs pack (monthly mode only). */
export function deriveExtraWholeHoursFromUser(
  u: {
    role?: string;
    compute_limit_mode?: string;
    compute_budget_ms_per_month?: number | null;
  },
  presets?: RoleComputePresetDTO[]
): number {
  const mode = (u.compute_limit_mode || '').toLowerCase();
  if (mode !== 'monthly') return 0;
  if (u.compute_budget_ms_per_month == null || u.compute_budget_ms_per_month <= 0) return 0;
  const packMs = presets
    ? presetForRole(presets, u.role).monthly_pack_hours * 3_600_000
    : packMonthlyMs(BILLING_TIERS[tierKeyFromRole(u.role)]);
  const raw = Math.round((u.compute_budget_ms_per_month - packMs) / 3_600_000);
  return Math.max(0, raw);
}
